import { Injectable } from '@angular/core';
import { Schedule, ScheduleConfig, ScheduleRole, Schedule_Paging } from '@app/models/schedule.model';
import { ApiService } from '@app/services/api.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { map, mergeMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import * as moment from 'moment';
import { ScheduleFilterScreen } from '@app/shared/enum/schedule';
import { AddSchedules, FetchDailyScheduleList, GetScheduleConfig, GetScheduleList, GetScheduleTimeLineList, RemoveVisibleOnlyData, ResetSchedule, ResetScheduleFilter, SetCurrentDate, SetExistingFilter, SetScheduleConfig, SetScheduleConfigRole, SetScheduleResult, SetVisibleOnlyData, TriggerHistory, UpdateConfigOpenState } from './schedule.classes';
import { AuthSelectors } from '../auth/auth.selectors';

export interface ScheduleStateModel {
  dailyScheduleList: Schedule[] | null;
  scheduleList: Schedule[] | null;
  scheduleList_Paging: { scheduleList: Schedule[] | null, takesRecord?: number } | null;
  currentDate: Date;
  scheduleConfig: ScheduleConfig;
  scheduleConfigOpen: boolean;
  scheduleConfigRole: ScheduleRole;
  fetchHistory: Date;
  onlyDataShowing?: string;
  existingFilter: boolean;
}
const localStorageFilterSetting = 'conditionFilter'
const localStorageScheduleSettingName = 'scheduleFilter'
@State<ScheduleStateModel>({
  name: 'schedule',
  defaults: {
    dailyScheduleList: null,
    scheduleList: null,
    scheduleList_Paging: null,
    currentDate: new Date(),
    scheduleConfig: new ScheduleConfig(),
    scheduleConfigOpen: false,
    scheduleConfigRole: new ScheduleRole(),
    fetchHistory: new Date(),
    // hide currentData state (focus 1 state at the time)
    onlyDataShowing: undefined,
    existingFilter: false
  },
})
@Injectable()
export class ScheduleState {
  constructor(private apiService: ApiService, private store: Store) { }
  @Action(ResetSchedule)
  cleanScheduleList(
    ctx: StateContext<ScheduleStateModel>
  ) {
    ctx.patchState({ scheduleList: [] });
  }

  @Action(AddSchedules)
  addSchedules(
    ctx: StateContext<ScheduleStateModel>,
    { schedules }: AddSchedules
  ) {
    const { scheduleList } = ctx.getState();
    let current = scheduleList || [];
    let reduceSchedule = [...current, ...schedules.filter(x => !current.some(curr => curr.noteid === x.noteid && curr.objcd === x.objcd))]
    ctx.patchState({ scheduleList: reduceSchedule });
  };


  @Action(FetchDailyScheduleList)
  fetchDailyScheduleList(
    ctx: StateContext<ScheduleStateModel>,
    { date }: FetchDailyScheduleList
  ) {
    const { currentDate } = ctx.getState();
    return this.apiService
      .getDailyScheduleList(date ?? currentDate)
      .pipe(tap((dailyScheduleList) => ctx.patchState({ dailyScheduleList })));
  }

  @Action(GetScheduleList)
  getScheduleList(
    ctx: StateContext<ScheduleStateModel>,
    { stfcd, sdate, edate }: GetScheduleList
  ) {
    var _skip = 0;  // start with skip rows = 0;
    var scheduleList: Schedule[] = []; // create variable scheduleList

    const handleResponse = (scheduleList_Paging: Schedule_Paging): Observable<Schedule[]> => {  // create handle response after each result returned from the api
      scheduleList = [...scheduleList, ...scheduleList_Paging.scheduleList]; // schedule concat with result (scheduleList)
      ctx.patchState({ scheduleList }); // path schedule in state

      if (scheduleList_Paging.scheduleList.length === 10) { // when result (scheduleList) have 10 records then recall api
        _skip = scheduleList_Paging.takesRecord || 0; // set skip = record scheduleList) got last api
        return this.getScheduleList_Paging(stfcd, sdate, edate, _skip).pipe( // call api
          mergeMap(handleResponse) // call handle response
        );
      } else { // when esult (scheduleList) have 10 reocords then recall api have less 10 records then return result and terminate the function's process
        return of(scheduleList);
      }
    };

    return this.getScheduleList_Paging(stfcd, sdate, edate, _skip).pipe( // call api
      mergeMap(handleResponse) // call handle response
    );
  }

  @Action(GetScheduleTimeLineList)
  getScheduleTimeLineList(
    ctx: StateContext<ScheduleStateModel>,
    { stfcd, sdate, edate, roleKey, roleContents }: GetScheduleTimeLineList
  ) {
    return this.apiService.getScheduleList({
      stfcd, startDate: sdate, endDate: edate, spinner: false, roleKey, roleContents
    }).pipe( // call api
      mergeMap((res: any) => {
        ctx.patchState({ scheduleList: res }); // path schedule in state
        return of(res)
      })
    );
  }

  @Action(UpdateConfigOpenState)
  updateConfigOpen(
    ctx: StateContext<ScheduleStateModel>,
    { open }: UpdateConfigOpenState
  ) {
    ctx.patchState({ scheduleConfigOpen: open })
  }

  @Action(SetVisibleOnlyData)
  updateVisible(
    ctx: StateContext<ScheduleStateModel>,
    { id }: SetVisibleOnlyData
  ) {
    ctx.patchState({ onlyDataShowing: id })
  }

  @Action(RemoveVisibleOnlyData)
  removeVisible(
    ctx: StateContext<ScheduleStateModel>,
    { id }: RemoveVisibleOnlyData
  ) {
    ctx.patchState({ onlyDataShowing: id ? id : undefined })
  }

  /**
   * function handle call api get schedule List
   * @param stfcd 
   * @param sdate 
   * @param edate 
   * @param skip 
   * @returns 
   */
  private getScheduleList_Paging(stfcd: string, sdate: Date, edate: Date, skip: number): Observable<Schedule_Paging> {
    return this.apiService.getScheduleList({ stfcd, startDate: sdate, endDate: edate, skip });
  }

  @Action(SetScheduleResult)
  setScheduleResult(
    ctx: StateContext<ScheduleStateModel>,
    { note }: SetScheduleResult
  ) {
    const { objkbn, objcd, noteid, resultatr, displayOption, upddate } = note;
    const { dailyScheduleList: list } = ctx.getState();
    const dailyScheduleList =
      list?.map((schedule) =>
        schedule.objkbn === objkbn &&
          schedule.objcd === objcd &&
          schedule.noteid === noteid
          ? { ...schedule, resultatr, displayOption, upddate }
          : schedule
      ) ?? null;
    ctx.patchState({ dailyScheduleList });
  }

  @Action(SetCurrentDate)
  setCurrentDate(
    ctx: StateContext<ScheduleStateModel>,
    { currentDate }: SetCurrentDate
  ) {
    ctx.patchState({ currentDate });
    let { scheduleConfig } = ctx.getState();
    let newConfig: any = { ...scheduleConfig };
    newConfig.targetDate = moment(currentDate).startOf('days').toDate();
    this.setFilter(ctx, { config: newConfig });
  }

  @Action(GetScheduleConfig)
  getFilter(
    ctx: StateContext<ScheduleStateModel>,
    { }: GetScheduleConfig
  ) {
    try {
      const profile = this.store.selectSnapshot(AuthSelectors.profile);
      const currentUnit = this.store.selectSnapshot(AuthSelectors.selectedUnit);
      const request = {
        CompanyId: currentUnit?.companyId.toString(),
        StaffId: profile?.staffCode,
        ScreenName: ScheduleFilterScreen.SCHEDULE
      };
      this.apiService.getStfBDSetting(request).pipe(
        map((res: any) => {
          if (!res?.setting) {
            return new ScheduleConfig();
          }
          return JSON.parse(res.setting);
        }),
        tap((dataSetting: any) => {
          const setting = dataSetting.setting;
          if (!setting) return;
          
          let filter = new ScheduleConfig();
          filter.isUser = setting.scheduleType === "DAI";
          filter.isVertical = setting.timeAxis === "x";
          filter.users = setting.displayDai;
          filter.staffs = setting.displayStaff;
          filter.hasPlanning = setting.onlySchedule === "1";
          filter.stime = setting.stime;
          filter.etime = setting.etime;
          filter.byResultAtr = setting.resultatr;

          let local: any = localStorage.getItem(localStorageFilterSetting);
          let convert: any = {};
          if (local) {
            convert = JSON.parse(local) || {};
          }
          convert[localStorageScheduleSettingName] = filter;
          localStorage.setItem(localStorageFilterSetting, JSON.stringify(convert));

          ctx.patchState({ scheduleConfig: filter || new ScheduleConfig() });
          ctx.patchState({
              existingFilter: true
          });
        })
      ).subscribe();
    } catch (error) {
      // error filter
    }
  }


  @Action(ResetScheduleFilter)
  resetFilter(
    ctx: StateContext<ScheduleStateModel>,
    { }: ResetScheduleFilter
  ) {
    // localStorage.removeItem(localStorageFilterSetting);
    ctx.patchState({ scheduleConfig: new ScheduleConfig() });
  }

  @Action(SetScheduleConfig)
  setFilter(
    ctx: StateContext<ScheduleStateModel>,
    { config: filter }: SetScheduleConfig
  ) {
    if (filter) {
      try {
        let local: any = localStorage.getItem(localStorageFilterSetting);
        let convert: any = {};
        if (local) {
          convert = JSON.parse(local) || {};
        }
        convert[localStorageScheduleSettingName] = filter;
        localStorage.setItem(localStorageFilterSetting, JSON.stringify(convert));
      } catch (error) {
        // error set filter
      } finally {
        ctx.patchState({ scheduleConfig: filter });
      }
    }
  }

  @Action(SetScheduleConfigRole)
  setRole(
    ctx: StateContext<ScheduleStateModel>,
    { sched_add, sched_view }: SetScheduleConfigRole
  ) {
    ctx.patchState({ scheduleConfigRole: { sched_add, sched_view } })
  }
  @Action(TriggerHistory)
  triggerHistory(
    ctx: StateContext<ScheduleStateModel>,
    { }: TriggerHistory
  ) {
    ctx.patchState({ fetchHistory: new Date() });
  }

  @Action(SetExistingFilter)
  setExistingFilter(
    ctx: StateContext<ScheduleStateModel>,
    { existingFilter }: SetExistingFilter
  ) {
    ctx.patchState({ existingFilter })
  }
}
